import { CURRENT_USER_GROUP_ORGS } from "@/api/queries/currentUserGroupOrgs";
import { useEffect, useState } from "react";
import {
  useReactiveVar,
  useLazyQuery,
  useQuery,
  ApolloError,
} from "@apollo/client";
import { organizationsVar } from "@/reactiveVars/user";
import { CURRENT_USER_ORG } from "@/api/queries/currentUserOrg";
import { Organization } from "@/types/Organization";

export const PAGE_SIZE = 100;

export const useUserOrganizations = (): {
  data: Organization[] | null;
  loading: boolean;
  loadingProgress: { loaded: number; total: number } | null;
  error: ApolloError | undefined;
} => {
  const [getOrganizations, { error: groupOrgError }] = useLazyQuery(
    CURRENT_USER_GROUP_ORGS,
  );
  const { data: defaultOrgData, error: defaultOrgError } =
    useQuery(CURRENT_USER_ORG);

  const organizationsValue = useReactiveVar(organizationsVar.getReactiveVar());
  const [loadingProgress, setLoadingProgress] = useState<{
    loaded: number;
    total: number;
  } | null>(null);

  useEffect(() => {
    if (organizationsValue !== null) return;
    if (!defaultOrgData) return;

    const fetchAllItems = async () => {
      let hasNextPage = true;
      let cursor = null;
      const pages = [];
      let totalCount = 0;
      let loadedCount = 0;

      while (hasNextPage) {
        const { data } = await getOrganizations({
          variables: {
            after: cursor,
            first: PAGE_SIZE,
          },
        });

        const orgsConnection = data?.me?.officialOrganizations;

        if (totalCount === 0) {
          totalCount = orgsConnection?.totalCount ?? 0;
        }

        const organizations =
          orgsConnection?.edges?.map((edge) => edge?.node).filter((m) => !!m) ||
          [];

        pages.push(organizations);
        loadedCount += organizations.length;

        if (totalCount > 0) {
          setLoadingProgress({ loaded: loadedCount, total: totalCount });
        }

        hasNextPage = !!orgsConnection?.pageInfo.hasNextPage;
        cursor = orgsConnection?.pageInfo.endCursor;
      }

      const groupOrgs = pages.flat();

      const defaultOrg = defaultOrgData?.me.officialOrganization;

      // move default org to front of list (if it exists)
      const orgList = groupOrgs.filter((org) => org.id !== defaultOrg?.id);
      if (defaultOrg) orgList.unshift(defaultOrg);

      organizationsVar.set(orgList);

      setLoadingProgress(null);
    };
    fetchAllItems();
  }, [getOrganizations, organizationsValue, defaultOrgData]);

  return {
    data: organizationsValue,
    loading: organizationsValue === null,
    loadingProgress,
    error: groupOrgError || defaultOrgError,
  };
};
